import type { AxiosInstance, AxiosResponse } from 'axios';
import Axios from 'axios';
import type { Router } from 'vue-router';
import SignOutService from '@/domain/authentication/services/SignOutService.ts';
import ApiError from '@/infrastructure/errors/ApiError.ts';
import { useSessionStore } from '@/stores/SessionStore.ts';
import MessageBag from '@/infrastructure/support/MessageBag.ts';

export default class Api {
  public static router: Router;
  protected client: AxiosInstance;
  private forwardError: boolean;
  private minDurationMs: number;

  constructor() {
    this.client = Axios.create({
      baseURL: import.meta.env.VITE_API_URL,
    });
  }

  async handleError(error): Promise<never> {
    const response = error.response;

    // Unauthorized or token mismatch: log the person out.
    if (response) {
      const code = response.data?.error?.code;
      if (response.status === 401 || code === 1000 || code === 1002) {
        new SignOutService().signOut().then((signedOut) => {
          if (signedOut) {
            window.location.replace('/');
          }
        });
        // The page reloads after sign-out; never settle so the caller's chain
        // does not run using the cleared session.
        return new Promise<never>(() => {});
      }
    }

    if (this.forwardError && Api.router) {
      await Api.router.push({ name: 'ErrorPage' });
      // We navigated away; never settle so the caller's `.then`/`await` chain
      // does not run on `undefined` (and we avoid an unhandled rejection).
      return new Promise<never>(() => {});
    }

    if (response) {
      throw Api.mapError(response.data, response.status);
    }

    throw new ApiError(
      error.number,
      new MessageBag({
        errors: [error.message],
      }),
    );
  }

  setMinimumDurationMsForCalls(minimumDurationMs: number): this {
    this.minDurationMs = minimumDurationMs;
    return this;
  }

  forwardToErrorPageOnException(forwardError = true) {
    this.forwardError = forwardError;
    return this;
  }

  standardGet<T = any>(
    url: string,
    params: any,
    includes: Array<any> = [],
  ): Promise<AxiosResponse<T>> {
    if (includes.length > 0) {
      params.with = includes.join();
    }

    const callPromise = this.client
      .get(url, {
        params: params,
        headers: this._standardHeader(),
      })
      .catch((error) => this.handleError(error));
    return this._applyMinimumDurationIfSet(callPromise);
  }

  extractArray<T = any>(rsp): T[] {
    const body = rsp?.data;
    if (Array.isArray(body)) {
      return body;
    }
    return Array.isArray(body?.data) ? body.data : [];
  }

  standardPost<T = any>(url, payload, params = {}): Promise<AxiosResponse<T>> {
    const callPromise = this.client
      .post(url, payload, {
        params: params,
        headers: this._standardHeader(),
      })
      .catch((error) => this.handleError(error));
    return this._applyMinimumDurationIfSet(callPromise);
  }

  standardPut<T = any>(url, payload, params = {}): Promise<AxiosResponse<T>> {
    const callPromise = this.client
      .put(url, payload, {
        params: params,
        headers: this._standardHeader(),
      })
      .catch((error) => this.handleError(error));
    return this._applyMinimumDurationIfSet(callPromise);
  }

  standardDelete<T = any>(url, params = {}): Promise<AxiosResponse<T>> {
    const callPromise = this.client
      .delete(url, {
        params: params,
        headers: this._standardHeader(),
      })
      .catch((error) => this.handleError(error));
    return this._applyMinimumDurationIfSet(callPromise);
  }

  standardRequest(method, url, params = {}) {
    const callPromise = this.client
      .request({
        method: method,
        url: url,
        params: params,
        data: {},
        headers: this._standardHeader(),
      })
      .catch((error) => this.handleError(error));
    return this._applyMinimumDurationIfSet(callPromise);
  }

  standardDownload(url) {
    const callPromise = this.client({
      url: url, //your url
      method: 'GET',
      responseType: 'blob', // important
      headers: this._standardHeader(),
    })
      .then((response) => {
        const filename = this._getFilenameFromDispositionHeader(
          // !!! CORS headers must be set in Metropolis
          response.headers['content-disposition'],
        );
        // create file link in browser's memory
        const href = URL.createObjectURL(response.data);

        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', filename ?? 'download'); //or any other extension
        document.body.appendChild(link);
        link.click();

        // clean up "a" element & remove ObjectURL
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      })
      .catch((error) => this.handleError(error));
    return this._applyMinimumDurationIfSet(callPromise);
  }

  _standardHeader() {
    return {
      Authorization: `Bearer ${useSessionStore().accessToken.value}`,
      'X-Client-Id': 'Atlantis',
    };
  }

  _standardHeaderNoAuth() {
    return {
      'X-Client-Id': 'Atlantis',
    };
  }

  _applyMinimumDurationIfSet(callPromise) {
    if (this.minDurationMs) {
      return (
        Promise.all([
          callPromise,
          new Promise((resolve) => setTimeout(resolve, this.minDurationMs)),
        ])
          // hide to result from the timeout-promise
          .then((results) => results[0])
      );
    } else {
      return callPromise;
    }
  }

  _getFilenameFromDispositionHeader(disposition) {
    // The header is absent when content-disposition isn't CORS-exposed.
    if (!disposition) {
      return null;
    }

    const utf8FilenameRegex = /filename\*=UTF-8''([\w%\-.]+)(?:; ?|$)/i;
    const asciiFilenameRegex = /^filename=(["']?)(.*?[^\\])\1(?:; ?|$)/i;

    let fileName = null;
    if (utf8FilenameRegex.test(disposition)) {
      fileName = decodeURIComponent(utf8FilenameRegex.exec(disposition)[1]);
    } else {
      // prevent ReDos attacks by anchoring the ascii regex to string start and
      //  slicing off everything before 'filename='
      const filenameStart = disposition.toLowerCase().indexOf('filename=');
      if (filenameStart >= 0) {
        const partialDisposition = disposition.slice(filenameStart);
        const matches = asciiFilenameRegex.exec(partialDisposition);
        if (matches != null && matches[2]) {
          fileName = matches[2];
        }
      }
    }
    return fileName;
  }

  private static mapError(data: any, status?: number): ApiError {
    if (data?.error) {
      return new ApiError(data.error.code, new MessageBag(data.error.messages));
    }

    // Generic error when no data.error
    return new ApiError(
      status ?? -1,
      new MessageBag({ errors: ['An unexpected error occurred'] }),
    );
  }
}
